import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // RBAC: effective permissions for the logged-in user, + module→paths map.
  const [permissions, setPermissions] = useState(null); // { [moduleKey]: {view,create,edit,delete} }
  const [permIsOwner, setPermIsOwner] = useState(false);
  const [modulePaths, setModulePaths] = useState([]);    // [{ key, paths }]
  const [permsLoaded, setPermsLoaded] = useState(false);
  const sessionPinged = useRef(false);

  const loadPermissions = React.useCallback(async () => {
    try {
      const res = await api.get('/access-control/me');
      const d = res.data?.data || {};
      setPermissions(d.permissions || {});
      setPermIsOwner(!!d.isOwner);
      setModulePaths(Array.isArray(d.modules) ? d.modules : []);
    } catch (e) {
      // Fail-open on the frontend: if the matrix can't load, fall back to the
      // legacy role-based gates (allowedRoles / nav roles) so the app still works.
      setPermissions(null);
    } finally {
      setPermsLoaded(true);
    }
  }, []);
  // Capture the real pathname before React Router's catch-all replaces it with /login
  const initialPathname = useRef(window.location.pathname);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
        // Guard against StrictMode double-mount
        if (!sessionPinged.current) {
          sessionPinged.current = true;
          api.get('/users/me', { headers: { 'x-current-page': initialPathname.current } }).catch((err) => {
            if (err.response?.status === 401 || err.response?.status === 403) {
              setUser(null);
              localStorage.removeItem('user');
            }
          });
          loadPermissions();
        }
      } catch (error) {
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      const response = await api.post('/users/login', { username, password });
      const userData = response.data.data.user;
      console.log('🔍 AuthContext - API Response:', response.data);
      console.log('🔍 AuthContext - userData:', userData);
      console.log('🔍 AuthContext - mustChangePassword:', userData.mustChangePassword);
      
      // Only set user if password change is NOT required
      // This prevents auto-redirect when password change dialog should show
      if (!userData.mustChangePassword) {
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        loadPermissions();
      }
      
      const result = { success: true, userData: userData };
      console.log('🔍 AuthContext - Returning:', result);
      return result;
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const logout = () => {
    // Flush page activity before clearing session
    try {
      const STORAGE_KEY = 'jbde_page_activity';
      const activity = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (activity && activity.pages && Object.keys(activity.pages).length) {
        const pages = Object.entries(activity.pages).map(([page, { count, lastTime }]) => ({
          page, count, time: lastTime,
        }));
        const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1'}/users/page-batch`;
        const userStr = localStorage.getItem('user');
        const token = localStorage.getItem('token');
        // Use synchronous XHR so it completes before session is cleared
        const xhr = new XMLHttpRequest();
        xhr.open('POST', apiUrl, false); // false = synchronous
        xhr.setRequestHeader('Content-Type', 'application/json');
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        if (userStr) {
          try {
            const u = JSON.parse(userStr);
            if (u.role)     xhr.setRequestHeader('x-user-role', u.role);
            if (u.id)       xhr.setRequestHeader('x-user-id', u.id);
            if (u.username) xhr.setRequestHeader('x-user-name', u.username);
          } catch {}
        }
        xhr.send(JSON.stringify({ date: activity.date, pages }));
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {}
    setUser(null);
    localStorage.removeItem('user');
    setPermissions(null);
    setPermIsOwner(false);
    setPermsLoaded(false);
  };

  const isOwner = () => permIsOwner || user?.role === 'owner';

  // can(moduleKey, action) — RBAC check. Owner always true. If the matrix hasn't
  // loaded (permissions === null) we return null so callers can fall back to the
  // legacy role gates.
  const can = (moduleKey, action = 'view') => {
    if (isOwner()) return true;
    if (!permissions) return null; // not loaded → caller falls back to roles
    return !!permissions[moduleKey]?.[action];
  };

  // Resolve a route path to its module key (longest-prefix match).
  const moduleForPath = (pathname) => {
    let best = null;
    for (const m of modulePaths) {
      for (const p of m.paths) {
        const match = p === '/' ? pathname === '/' : (pathname === p || pathname.startsWith(p + '/'));
        if (match && (!best || p.length > best.len)) best = { key: m.key, len: p.length };
      }
    }
    return best ? best.key : null;
  };

  // Can the user VIEW this route path? null if unknown/not-loaded (fall back).
  const canViewPath = (pathname) => {
    const key = moduleForPath(pathname);
    if (!key) return null;
    return can(key, 'view');
  };

  const hasRole = (roles) => {
    if (!user) return false;
    
    // Get user's roles - support both single role and roles array
    const userRoles = user.roles && user.roles.length > 0 ? user.roles : [user.role];
    
    // Check if roles parameter is a string or array
    if (typeof roles === 'string') {
      return userRoles.includes(roles);
    }
    
    // Check if user has ANY of the required roles
    return roles.some(role => userRoles.includes(role));
  };

  const isAdmin = () => hasRole(['owner', 'manager']);

  const hasAnyRole = (roles) => {
    // Alias for hasRole for clarity
    return hasRole(roles);
  };

  const hasAllRoles = (roles) => {
    if (!user) return false;
    const userRoles = user.roles && user.roles.length > 0 ? user.roles : [user.role];
    return roles.every(role => userRoles.includes(role));
  };

  const value = {
    user,
    loading,
    login,
    logout,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    isAdmin,
    isAuthenticated: !!user,
    // RBAC
    permissions,
    permsLoaded,
    can,
    isOwner,
    moduleForPath,
    canViewPath,
    reloadPermissions: loadPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
